import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  Mail,
  MapPin,
  Phone,
  Plus,
  Save,
  X,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useToast } from "@/shared/providers/ToastProvider";
import { ApiError } from "@/shared/lib/apiClient";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass, selectClass, textareaClass } from "../components/FormField";
import { provinceOptions, statusOptions } from "../data";
import {
  createAdminBranch,
  fetchAdminBranch,
  updateAdminBranch,
} from "../api/adminApi";
import { getAuthToken } from "../auth";

type BranchFormState = {
  name: string;
  province: string;
  address: string;
  phone: string;
  email: string;
  status: string;
};

const emptyForm: BranchFormState = {
  name: "",
  province: "",
  address: "",
  phone: "",
  email: "",
  status: "active",
};

export function BranchFormPage({ mode }: { mode: "create" | "edit" }) {
  const { t } = useTranslation();
  const labels = t.safecheck.branchesPage.create;
  const { showToast } = useToast();
  const navigate = useNavigate();
  const params = useParams();
  const branchId = mode === "edit" ? Number(params.id) : null;

  const [form, setForm] = useState<BranchFormState>(emptyForm);
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setField = (key: keyof BranchFormState, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  useEffect(() => {
    if (mode !== "edit" || !branchId || Number.isNaN(branchId)) {
      setLoading(false);
      return;
    }
    if (!getAuthToken()) {
      setError("Connexion admin requise.");
      setLoading(false);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const branch = await fetchAdminBranch(branchId);
        if (cancelled) return;
        setForm({
          name: branch.name ?? "",
          province: branch.province ?? "",
          address: branch.address ?? "",
          phone: branch.phone ?? "",
          email: branch.email ?? "",
          status: branch.status ?? "active",
        });
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Impossible de charger l'antenne.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mode, branchId]);

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setError("Le nom est obligatoire.");
      return;
    }
    if (!getAuthToken()) {
      setError("Connexion admin requise.");
      return;
    }

    setSaving(true);
    setError(null);
    const payload = {
      name: form.name.trim(),
      province: form.province,
      address: form.address,
      phone: form.phone,
      email: form.email,
      status: form.status,
    };

    try {
      if (mode === "edit" && branchId) {
        await updateAdminBranch(branchId, payload);
      } else {
        await createAdminBranch(payload);
      }
      showToast(t.actions.saved);
      navigate("/dashboard/branches");
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Enregistrement impossible.";
      setError(message);
      showToast(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-muted">Chargement…</p>;
  }

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t.safecheck.nav.branches, href: "/dashboard/branches" },
          { label: mode === "edit" ? "Modifier l'antenne" : labels.title },
        ]}
      />

      <div className="mb-4 flex justify-end">
        <Link
          to="/dashboard/branches"
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
        >
          <ArrowLeft size={14} />
          {t.safecheck.back}
        </Link>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-pink/30 bg-pink-soft px-4 py-3 text-sm text-pink">
          {error}
        </div>
      )}

      <div className="theme-card p-6">
        <div className="mb-6 flex items-center gap-2 text-base font-semibold text-foreground">
          <Plus size={16} className="text-primary" />
          {labels.cardTitle}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormField label={labels.fields.name} required>
            <div className="relative">
              <Building2 size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                value={form.name}
                onChange={(event) => setField("name", event.target.value)}
                placeholder={labels.placeholders.name}
                className={`${inputClass} pl-9`}
              />
            </div>
          </FormField>

          <FormField label={labels.fields.province}>
            <div className="relative">
              <MapPin size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <select
                value={form.province}
                onChange={(event) => setField("province", event.target.value)}
                className={`${selectClass} pl-9`}
              >
                <option value="">{labels.placeholders.province}</option>
                {provinceOptions
                  .filter((option) => option.value)
                  .map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
              </select>
            </div>
          </FormField>

          <FormField label={labels.fields.address} className="md:col-span-2">
            <div className="relative">
              <MapPin size={14} className="pointer-events-none absolute left-3 top-3 text-muted" />
              <textarea
                value={form.address}
                onChange={(event) => setField("address", event.target.value)}
                placeholder={labels.placeholders.address}
                className={`${textareaClass} min-h-[84px] pl-9`}
              />
            </div>
          </FormField>

          <FormField label={labels.fields.phone}>
            <div className="relative">
              <Phone size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                value={form.phone}
                onChange={(event) => setField("phone", event.target.value)}
                placeholder={labels.placeholders.phone}
                className={`${inputClass} pl-9`}
              />
            </div>
          </FormField>

          <FormField label={labels.fields.email}>
            <div className="relative">
              <Mail size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                type="email"
                value={form.email}
                onChange={(event) => setField("email", event.target.value)}
                placeholder={labels.placeholders.email}
                className={`${inputClass} pl-9`}
              />
            </div>
          </FormField>

          <FormField label={labels.fields.status}>
            <div className="relative">
              <CheckCircle2 size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <select
                value={form.status}
                onChange={(event) => setField("status", event.target.value)}
                className={`${selectClass} pl-9`}
              >
                {statusOptions
                  .filter((option) => option.value)
                  .map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
              </select>
            </div>
          </FormField>
        </div>

        <div className="mt-8 border-t border-border pt-6">
          <div className="flex flex-wrap items-center justify-end gap-3">
            <Link
              to="/dashboard/branches"
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
            >
              <X size={14} />
              {t.safecheck.cancel}
            </Link>
            <button
              type="button"
              disabled={saving}
              onClick={() => void handleSubmit()}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-60"
            >
              <Save size={14} />
              {saving ? "…" : labels.submit}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export function AddBranchPage() {
  return <BranchFormPage mode="create" />;
}

export function EditBranchPage() {
  return <BranchFormPage mode="edit" />;
}
